import { useEffect, useRef, useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { ViewSwitch } from "@/components/ViewSwitch";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { assetUrl } from "@/lib/prefs";
import { BarChart3, CalendarDays, ChevronDown, Flame, FlaskConical, Gauge, Globe2, History, ImageUp, ListOrdered, LogOut, Menu, MoreHorizontal, Settings2, Shield, Shuffle, Trophy, UserRound, Users, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { HeaderSearch } from "@/components/shell/HeaderSearch";
import { Notifications } from "@/components/shell/Notifications";
import { SiteFooter } from "@/components/shell/SiteFooter";
import { MobileTabBar } from "@/components/shell/MobileTabBar";
import { useFreshNav } from "@/hooks/useFreshNav";
import "@/components/shell/shell.css";

const LINKS = [
  { to: "/analysis", label: "Analysis", icon: BarChart3, id: "nav-analysis", adminOnly: true },
  { to: "/europe", label: "Europe", icon: Globe2, id: "nav-europe" },
  { to: "/accuracy", label: "Accuracy", icon: Gauge, id: "nav-accuracy", adminOnly: true },
  { to: "/rotation", label: "Rotation Sim", icon: Shuffle, id: "nav-rotation", admin: true },
];

// Less-frequent admin tools, tucked into the "More" dropdown to keep the bar clean.
const MORE_LINKS = [
  { to: "/season-model", label: "New Model", icon: FlaskConical, id: "nav-season-model", admin: true },
  { to: "/rotations", label: "Rotations", icon: Users, id: "nav-rotations", adminOnly: true },
];

const canSee = (l, user, features) => (!l.admin || (user?.role === "admin" && features.rotation_sim))
  && (!l.adminOnly || user?.role === "admin") && (!l.userOnly || user?.role !== "admin");

export const visibleLinks = (user, features) => [...LINKS, ...MORE_LINKS].filter((l) => canSee(l, user, features));
const visibleMore = (user, features) => MORE_LINKS.filter((l) => canSee(l, user, features));

// Close a dropdown on outside click or Escape.
function useDismiss(open, close) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open, close]);
  return ref;
}

const HALFTIME_ITEMS = [
  { key: "players", label: "Παίκτες", icon: Users, to: "/halftime-value?mode=players" },
  { key: "teams", label: "Ομάδες", icon: Shield, to: "/halftime-value?mode=teams" },
  { key: "history", label: "Ιστορικό", icon: History, to: "/halftime-value?mode=history" },
  { key: "screenshot", label: "Screenshot", icon: ImageUp, to: "/halftime-value?mode=players", sep: true },
];

const STATS_ITEMS = [
  { key: "team", label: "Teams", icon: Shield, to: "/teams" },
  { key: "players", label: "Players Stats", icon: Trophy, to: "/stats" },
];

const STANDINGS_ITEMS = [
  { key: "standings", label: "Standings", icon: ListOrdered, to: "/standings" },
  { key: "schedule", label: "Schedule", icon: CalendarDays, to: "/schedule" },
];

function StandingsMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ref = useDismiss(open, () => setOpen(false));
  const active = pathname.startsWith("/standings") || pathname.startsWith("/schedule");
  const go = (to) => { setOpen(false); navigate(to); };
  return (
    <div className="nbc-dd" ref={ref}>
      <button type="button" className={`nbc-link nbc-dd-btn ${active ? "active" : ""}`} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)} data-testid="nav-standings">
        <ListOrdered className="h-4 w-4" /><span>Standings</span><ChevronDown className="nbc-dd-caret" size={14} />
      </button>
      {open && (
        <div className="nbc-dd-menu" role="menu" data-testid="nav-standings-menu">
          {STANDINGS_ITEMS.map(({ key, label, icon: Icon, to }) => (
            <div key={key} className="nbc-dd-item-wrap">
              <button type="button" role="menuitem" className="nbc-dd-item" onClick={() => go(to)} data-testid={`nav-standings-${key}`}>
                <Icon size={16} /><span>{label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatsMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ref = useDismiss(open, () => setOpen(false));
  const active = pathname.startsWith("/teams") || pathname.startsWith("/stats");
  const go = (to) => { setOpen(false); navigate(to); };
  return (
    <div className="nbc-dd" ref={ref}>
      <button type="button" className={`nbc-link nbc-dd-btn ${active ? "active" : ""}`} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)} data-testid="nav-stats">
        <Trophy className="h-4 w-4" /><span>Stats</span><ChevronDown className="nbc-dd-caret" size={14} />
      </button>
      {open && (
        <div className="nbc-dd-menu" role="menu" data-testid="nav-stats-menu">
          {STATS_ITEMS.map(({ key, label, icon: Icon, to }) => (
            <div key={key} className="nbc-dd-item-wrap">
              <button type="button" role="menuitem" className="nbc-dd-item" onClick={() => go(to)} data-testid={`nav-stats-${key}`}>
                <Icon size={16} /><span>{label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HalfTimeMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const ref = useDismiss(open, () => setOpen(false));
  const active = pathname.startsWith("/halftime-value");
  const go = (to) => { setOpen(false); navigate(to); };
  return (
    <div className="nbc-dd" ref={ref}>
      <button type="button" className={`nbc-link nbc-dd-btn ${active ? "active" : ""}`} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)} data-testid="nav-halftime-value">
        <Flame className="h-4 w-4" /><span>Half-time Value</span><ChevronDown className="nbc-dd-caret" size={14} />
      </button>
      {open && (
        <div className="nbc-dd-menu" role="menu" data-testid="nav-halftime-menu">
          {HALFTIME_ITEMS.map(({ key, label, icon: Icon, to, sep }) => (
            <div key={key} className={sep ? "nbc-dd-item-wrap is-sep" : "nbc-dd-item-wrap"}>
              <button type="button" role="menuitem" className="nbc-dd-item" onClick={() => go(to)} data-testid={`nav-halftime-${key}`}>
                <Icon size={16} /><span>{label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const ref = useDismiss(open, () => setOpen(false));
  if (!items.length) return null;
  const active = items.some((l) => pathname.startsWith(l.to));
  return (
    <div className="nbc-dd" ref={ref}>
      <button type="button" className={`nbc-link nbc-dd-btn ${active ? "active" : ""}`} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)} data-testid="nav-more">
        <MoreHorizontal className="h-4 w-4" /><span>More</span><ChevronDown className="nbc-dd-caret" size={14} />
      </button>
      {open && (
        <div className="nbc-dd-menu" role="menu" data-testid="nav-more-menu">
          {items.map(({ to, label, icon: Icon, id }) => (
            <div key={to} className="nbc-dd-item-wrap">
              <NavLink to={to} role="menuitem" className="nbc-dd-item" onClick={() => setOpen(false)} data-testid={id}>
                <Icon size={16} /><span>{label}</span>
              </NavLink>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccountMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);
  const navigate = useNavigate();
  const ref = useDismiss(open, () => setOpen(false));
  const admin = user?.role === "admin";
  const initials = (user?.name || user?.email || "?").trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const go = (to) => { setOpen(false); navigate(to); };
  return (
    <div className="nbc-dd nbc-acct" ref={ref}>
      <button type="button" className="nbc-avatar" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid="user-badge" title="Account">
        {user?.picture && !broken
          ? <img src={assetUrl(user.picture)} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} data-testid="user-avatar" />
          : <span data-testid="user-initials">{initials}</span>}
      </button>
      {open && (
        <div className="nbc-dd-menu nbc-dd-right" role="menu" data-testid="account-menu">
          <div className="nbc-acct-head">
            <strong>{user?.name || "Account"}</strong>
            {user?.email && <small>{user.email}</small>}
          </div>
          {!admin && (
            <button type="button" role="menuitem" className="nbc-dd-item" onClick={() => go("/account")} data-testid="nav-account">
              <UserRound size={16} /><span>My Account</span>
            </button>
          )}
          {admin && (
            <button type="button" role="menuitem" className="nbc-dd-item" onClick={() => go("/settings")} data-testid="nav-settings">
              <Settings2 size={16} /><span>Settings &amp; Log</span>
            </button>
          )}
          <div className="nbc-dd-item-wrap is-sep">
            <button type="button" role="menuitem" className="nbc-dd-item is-danger" onClick={() => { setOpen(false); onLogout(); }} data-testid="logout-btn">
              <LogOut size={16} /><span>Log out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AppShell({ children }) {
  const { logout, user } = useAuth();
  const [features, setFeatures] = useState({});
  const [menu, setMenu] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { api.get("/features").then((r) => setFeatures(r.data)).catch(() => {}); }, []);
  useEffect(() => { setMenu(false); }, [pathname]);
  const navigate = useNavigate();
  const fresh = useFreshNav();
  const links = visibleLinks(user, features);
  const navLinks = links.filter((l) => LINKS.includes(l));
  const moreLinks = visibleMore(user, features);
  return (
    <div className="min-h-screen court-grain nbc-shell" id="top">
      <header className="nbc-header" data-testid="site-header">
        <div className="nbc-header-inner">
          <button type="button" className="nbc-burger" aria-label="Toggle navigation" aria-expanded={menu} onClick={() => setMenu((v) => !v)} data-testid="nav-burger">
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
          <NavLink to="/" onClick={fresh("/")} data-testid="brand-link" className="nbc-brand-link" aria-label="NBACUBE home"><BrandLogo size="sm" /></NavLink>
          <nav className={`nbc-nav ${menu ? "open" : ""}`} aria-label="Main navigation" data-testid="main-nav">
            <ViewSwitch />
            <StandingsMenu />
            <StatsMenu />
            {navLinks.map(({ to, label, icon: Icon, id }) => (
              <NavLink key={to} to={to} data-testid={id} className={({ isActive }) => `nbc-link ${isActive ? "active" : ""}`}>
                <Icon className="h-4 w-4" /><span>{label}</span>
              </NavLink>
            ))}
            {user?.role === "admin" && <HalfTimeMenu />}
            <MoreMenu items={moreLinks} />
          </nav>
          <div className="nbc-actions">
            <HeaderSearch />
            <Notifications />
            <AccountMenu user={user} onLogout={async () => { await logout(); navigate("/login"); }} />
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 fade-up">{children}</main>
      <SiteFooter links={links} />
      <MobileTabBar />
    </div>
  );
}

export function PageTitle({ overline, title, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end gap-4 mb-8">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">{overline}</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-slate-100 leading-none mt-1">{title}</h1>
      </div>
      <div className="md:ml-auto flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function Panel({ children, className = "", ...rest }) {
  return <section {...rest} className={`rounded-lg border border-slate-800 bg-[#121722]/90 ${className}`}>{children}</section>;
}
